import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import DateFilter from "@/components/admin/DateFilter";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Circle, Clock, MapPin } from "lucide-react";

const STATUS_COLOR = {
  pending_payment: "#eab308",
  confirmed: "#3b82f6",
  assigned: "#8b5cf6",
  in_progress: "#f97316",
  completed: "#10b981",
  cancelled: "#94a3b8",
  refunded: "#64748b",
};

export default function Calendar() {
  const { t, lang } = useLang();
  // Iter 23 Sub-Fase A: default agenda = 3 dias antes + hoje + 3 dias depois (7 dias)
  const [filter, setFilter] = useState(() => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const before = new Date(now); before.setDate(now.getDate() - 3);
    const after = new Date(now); after.setDate(now.getDate() + 3);
    return { preset: "custom", from: iso(before), to: iso(after) };
  });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!filter.from || !filter.to) return;
    setLoading(true);
    api.get("/admin/bookings", { params: { from_date: filter.from, to_date: filter.to, limit: 1000 } })
      .then((r) => setBookings(r.data.items || r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filter.from, filter.to]);

  // Group by day
  const byDay = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      const d = (b.service_datetime || "").slice(0, 10);
      if (!d) continue;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(b);
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.service_datetime || "").localeCompare(b.service_datetime || ""));
    return map;
  }, [bookings]);

  const days = useMemo(() => {
    if (!filter.from || !filter.to) return [];
    const out = [];
    const s = new Date(filter.from);
    const e = new Date(filter.to);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      out.push(new Date(d));
    }
    return out;
  }, [filter.from, filter.to]);

  const iso = (d) => d.toISOString().slice(0, 10);
  const shiftDays = (delta) => {
    if (!filter.from || !filter.to) return;
    const s = new Date(filter.from); s.setDate(s.getDate() + delta);
    const e = new Date(filter.to); e.setDate(e.getDate() + delta);
    setFilter({ ...filter, preset: "custom", from: iso(s), to: iso(e) });
  };
  const resetToToday = () => {
    const now = new Date();
    const before = new Date(now); before.setDate(now.getDate() - 3);
    const after = new Date(now); after.setDate(now.getDate() + 3);
    setFilter({ preset: "custom", from: iso(before), to: iso(after) });
  };

  return (
    <div className="p-10">
      <PageHeader
        eyebrow={t("overview")}
        title={t("nav_calendar")}
        description="Vista de 7 dias (3 antes · hoje · 3 depois). Use os presets para mudar o período."
      >
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDays(-7)} className="w-8 h-8 border border-slate-300 flex items-center justify-center hover:bg-slate-50" data-testid="cal-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={resetToToday} className="h-8 px-3 text-[10px] uppercase tracking-widest font-bold border border-[#f59e0b] text-[#f59e0b] hover:bg-amber-50" data-testid="cal-today">{t("view_today")}</button>
          <button onClick={() => shiftDays(7)} className="w-8 h-8 border border-slate-300 flex items-center justify-center hover:bg-slate-50" data-testid="cal-next-week"><ChevronRight className="w-4 h-4" /></button>
          <DateFilter value={filter} onChange={setFilter} testId="calendar-filter" />
        </div>
      </PageHeader>

      {loading && <div className="text-sm text-slate-500 py-6">{t("loading")}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-3">
          {days.length === 0 && !loading && (
            <div className="border border-slate-200 p-8 text-center text-slate-500 text-sm">Sem dados</div>
          )}
          {days.map((d) => {
            const key = d.toISOString().slice(0, 10);
            const items = byDay.get(key) || [];
            const isToday = key === new Date().toISOString().slice(0, 10);
            return (
              <div key={key} className={`border ${isToday ? "border-[#f59e0b]" : "border-slate-200"} bg-white`} data-testid={`day-${key}`}>
                <div className={`px-4 py-2 flex items-center justify-between border-b border-slate-200 ${isToday ? "bg-[#f59e0b]/10" : "bg-slate-50"}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="text-lg font-serif text-[#0a0f1e]">
                      {d.toLocaleDateString(lang === "PT" ? "pt-PT" : "en-GB", { weekday: "long", day: "numeric", month: "short" })}
                    </span>
                    {isToday && <span className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">{t("today_short")}</span>}
                  </div>
                  <span className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">{items.length} {t("bookings_count")}</span>
                </div>
                {items.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-slate-400">—</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {items.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => setSelected(b)}
                        data-testid={`event-${b.id}`}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-4"
                      >
                        <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLOR[b.status] || "#94a3b8" }} />
                        <span className="font-mono text-[11px] text-slate-600 w-14">{(b.service_datetime || "").slice(11, 16)}</span>
                        <span className="text-sm font-semibold text-[#0a0f1e] flex-1 truncate">{b.reference}</span>
                        <span className="text-xs text-slate-700 hidden md:inline truncate max-w-[280px]">{b.pickup_address} → {b.destination_address}</span>
                        <span className="text-xs text-slate-800 font-semibold w-20 text-right">€ {(b.client_pays ?? b.total_amount ?? 0).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-slate-200 bg-white sticky top-6" data-testid="calendar-detail">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Detalhe</div>
            </div>
            {selected ? (
              <div className="p-4 space-y-3 text-sm">
                <div>
                  <div className="font-serif text-lg text-[#0a0f1e]">{selected.reference}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-700 font-semibold mt-1">{selected.status}</div>
                </div>
                <div className="flex items-center gap-2 text-slate-800">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span>{new Date(selected.service_datetime).toLocaleString(lang === "PT" ? "pt-PT" : "en-GB")}</span>
                </div>
                <div className="flex items-start gap-2 text-slate-800">
                  <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                  <div>
                    <div className="font-semibold">{selected.pickup_address}</div>
                    <div className="text-slate-600">→ {selected.destination_address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-slate-800">
                  <Circle className="w-4 h-4 text-slate-500" />
                  <span>{selected.passengers} pax · {selected.luggage} bag(s)</span>
                </div>
                <div className="pt-3 border-t border-slate-100 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Total</span><span className="font-bold">€ {(selected.client_pays ?? selected.total_amount ?? 0).toFixed(2)}</span></div>
                  <div className="flex justify-between text-xs mt-1"><span className="text-slate-500">Motorista</span><span className="text-slate-700 font-medium">{selected.driver_id?.slice(0, 8) || "—"}</span></div>
                </div>
                <a href={`/admin/bookings/${selected.id}`} className="block text-center h-9 leading-9 bg-[#f59e0b] text-[#0a0f1e] text-xs uppercase tracking-widest font-bold hover:brightness-95">
                  Abrir reserva →
                </a>
              </div>
            ) : (
              <div className="p-6 text-center text-slate-500 text-sm">Clique numa reserva para ver detalhes.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
